import React, { useState, useEffect, useMemo, useRef } from 'react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import {
    Box, Button, Dialog, DialogActions, DialogContent, DialogTitle,
    Typography, IconButton, TextField, FormControl, InputLabel, Select, MenuItem,
    ToggleButtonGroup, ToggleButton, Autocomplete, Chip, Divider,
    CircularProgress, Card, CardMedia, CardContent, LinearProgress, Paper, Switch, FormControlLabel,
    Popover, Tooltip,
} from "@mui/material";
import {
    Cancel, Save, Fullscreen, FullscreenExit, ErrorOutline,
    FormatBold, FormatItalic, FormatStrikethrough, FormatUnderlined,
    Code, DataObject, Link as LinkIcon, VisibilityOff,
    Visibility, Edit, AttachFile, Delete, Image, VideoFile, AudioFile, Description,
    Add, Remove, Phone, OpenInNew, Reply, TextFields, ImageOutlined, Videocam, InsertDriveFile,
    EmojiEmotions,
    LocalOffer,
} from "@mui/icons-material";
import { WhatsAppBusinessConfig, WhatsAppBusinessButton } from "modules/Interfaces/InstanUpdates/Template";
import { Template } from "modules/Interfaces/InstanUpdates/Template";
import { apiClient } from "utils/ApiClient";
import { getSmsSegmentInfo } from "utils/smsEstimatedCreditCost";

const SYSTEM_TEMPLATE_PREFIX = 'IUST-';

/** Common emojis for SMS body — inserting any non–GSM-7 character switches encoding to UCS-2 in the segment counter */
const SMS_QUICK_EMOJIS: string[] = [
    '😀', '😃', '😄', '😁', '😅', '😂', '🙂', '😉', '😊', '😍', '🤩', '😘', '😋', '😛', '🤔', '😏', '😌', '😢', '😭', '😤', '😡', '🤗',
    '🤝', '👍', '👎', '👏', '🙏', '👋', '✌️', '🙌', '💪', '👆', '👇', '👉', '👈',
    '❤️', '💛', '💚', '💙', '💜', '🖤', '💔', '❣️', '💯', '🔥', '✨', '⭐', '🌟',
    '✅', '❌', '⚠️', '📌', '📍', '🔔', '📧', '📱', '💬', '🎉', '🎁', '✈️', '🏠',
    '⏰', '📅', '💰', '🔒', '🔑', '☀️', '🌙', '☁️', '🌈', '❄️', '☕', '🍕', '🎂',
];

const TEMPLATE_CATEGORIES = [
    { value: 'transactional', label: 'Transactional' },
    { value: 'promotional', label: 'Promotional' },
    { value: 'service', label: 'Service' },
    { value: 'system', label: 'System' },
];

type CountryOption = { code: string; label: string; dialCode: string };

interface SystemTemplateDialogProps {
    dialogOpen: boolean;
    closeDialog: () => void;
    isEditing: boolean;
    currentTemplate: Partial<Template> | null;
    setCurrentTemplate: React.Dispatch<React.SetStateAction<Partial<Template> | null>>;
    saveTemplate: () => Promise<void>;
    templateSchema: any;
    templateNameError: string;
    setTemplateNameError: (error: string) => void;
    saveError?: string;
    setSaveError?: (error: string) => void;
}

const stripSystemPrefix = (name: string | undefined): string => {
    if (!name) return '';
    return name.startsWith(SYSTEM_TEMPLATE_PREFIX)
        ? name.substring(SYSTEM_TEMPLATE_PREFIX.length)
        : name;
};

function escapeHtmlForSmsQuillPlain(s: string): string {
    return s
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;');
}

/** Plain text → HTML for ReactQuill; stored `templateContent` remains plain (via Quill getText). */
function plainTextToQuillHtml(plain: string): string {
    if (!plain) return '<p><br></p>';
    return plain
        .split('\n')
        .map((line) => `<p>${line ? escapeHtmlForSmsQuillPlain(line) : '<br>'}</p>`)
        .join('');
}

/** SMS has no bold/italic/etc.; only a custom emoji strip is shown above the editor. */
const SMS_QUILL_MODULES = { toolbar: false };

export const SystemTemplateDialog: React.FC<SystemTemplateDialogProps> = ({
    dialogOpen,
    closeDialog,
    isEditing,
    currentTemplate,
    setCurrentTemplate,
    saveTemplate,
    templateSchema,
    templateNameError,
    setTemplateNameError,
    saveError,
    setSaveError,
}) => {
    const [isMaximized, setIsMaximized] = useState(false);
    const [countries, setCountries] = useState<CountryOption[]>([]);
    const nameRegex = /^[a-zA-Z0-9_-]*$/;

    useEffect(() => {
        if (dialogOpen && countries.length === 0) {
            apiClient.get('/auth/countries').then((res: any) => {
                if (Array.isArray(res.data)) setCountries(res.data);
            }).catch(() => {});
        }
    }, [dialogOpen]);

    const excludedFields = ['_id', '__v', 'templateCreatedByUserId', 'templateUpdatedByUserId', 'templateUpdatedOn', 'createdAt', 'updatedAt', 'sharedTo'];

    const isSaveDisabled = () => {
        if (!currentTemplate) return true;
        if (templateNameError) return true;
        const requiredFields = (Object.keys(templateSchema.paths) as (keyof typeof templateSchema.paths)[])
            .filter(key => templateSchema.paths[key]?.isRequired && !excludedFields.includes(key as string));
        for (const field of requiredFields) {
            if (field === "templateContent") {
                if (typeof currentTemplate[field] !== 'string' || currentTemplate[field].trim() === '') return true;
            } else if (!currentTemplate[field as keyof Template]) {
                return true;
            }
        }
        return false;
    };

    const templateType = currentTemplate?.templateType;

    return (
        <Dialog
            open={dialogOpen}
            onClose={closeDialog}
            fullWidth
            maxWidth={isMaximized ? false : "md"}
            fullScreen={isMaximized}
            sx={{
                '& .MuiDialog-paper': {
                    margin: isMaximized ? 0 : 'auto',
                    maxWidth: isMaximized ? '100vw' : 'md',
                    maxHeight: isMaximized ? '100vh' : '90vh',
                    width: isMaximized ? '100vw' : 'auto',
                    height: isMaximized ? '100vh' : 'auto'
                }
            }}
        >
            <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pr: 1 }}>
                <Typography component="span" variant="h6">
                    {isEditing ? `Edit ${templateType?.toUpperCase()} System Template` : `Create New ${templateType?.toUpperCase()} System Template`}
                </Typography>
                <IconButton onClick={() => setIsMaximized(!isMaximized)} size="small" sx={{ ml: 1 }}>
                    {isMaximized ? <FullscreenExit /> : <Fullscreen />}
                </IconButton>
            </DialogTitle>
            <DialogContent sx={{
                height: isMaximized ? 'calc(100vh - 140px)' : 'auto',
                overflow: 'auto',
                display: 'flex',
                flexDirection: 'column'
            }}>
                {saveError && (
                    <Box sx={{ mb: 2 }}>
                        <Typography variant="body2" color="error" sx={{
                            display: 'flex', alignItems: 'center', gap: 1,
                            backgroundColor: 'rgba(244, 67, 54, 0.1)',
                            padding: 2, borderRadius: 1,
                            border: '1px solid rgba(244, 67, 54, 0.3)'
                        }}>
                            <ErrorOutline sx={{ fontSize: 20 }} />
                            {saveError}
                        </Typography>
                    </Box>
                )}

                {templateType === 'whatsapp' && (
                    <WhatsAppSystemEditor
                        currentTemplate={currentTemplate}
                        setCurrentTemplate={setCurrentTemplate}
                        templateNameError={templateNameError}
                        setTemplateNameError={setTemplateNameError}
                        nameRegex={nameRegex}
                        isEditing={isEditing}
                        onSaveError={setSaveError || (() => {})}
                        countries={countries}
                    />
                )}
                {(templateType === 'sms' || templateType === 'gsmText') && (
                    <SmsSystemEditor
                        dialogOpen={dialogOpen}
                        currentTemplate={currentTemplate}
                        setCurrentTemplate={setCurrentTemplate}
                        templateNameError={templateNameError}
                        setTemplateNameError={setTemplateNameError}
                        nameRegex={nameRegex}
                        countries={countries}
                    />
                )}
                {templateType === 'telegram' && (
                    <TelegramSystemEditor
                        currentTemplate={currentTemplate}
                        setCurrentTemplate={setCurrentTemplate}
                        templateNameError={templateNameError}
                        setTemplateNameError={setTemplateNameError}
                        nameRegex={nameRegex}
                        isEditing={isEditing}
                        onSaveError={setSaveError || (() => {})}
                        countries={countries}
                    />
                )}
                {templateType === 'whatsappbusiness' && (
                    <WhatsAppBusinessSystemEditor
                        currentTemplate={currentTemplate}
                        setCurrentTemplate={setCurrentTemplate}
                        templateNameError={templateNameError}
                        setTemplateNameError={setTemplateNameError}
                        isEditing={isEditing}
                        onSaveError={setSaveError || (() => {})}
                        countries={countries}
                    />
                )}
            </DialogContent>
            <DialogActions>
                <Button variant="outlined" color="secondary" onClick={closeDialog} startIcon={<Cancel />}>Cancel</Button>
                <Button onClick={saveTemplate} startIcon={<Save />} variant="contained" color="success" disabled={isSaveDisabled()}>Save</Button>
            </DialogActions>
        </Dialog>
    );
};

/* ───────── WhatsApp System Editor ───────── */

const WhatsAppSystemEditor: React.FC<{
    currentTemplate: Partial<Template> | null;
    setCurrentTemplate: React.Dispatch<React.SetStateAction<Partial<Template> | null>>;
    templateNameError: string;
    setTemplateNameError: (e: string) => void;
    nameRegex: RegExp;
    isEditing: boolean;
    onSaveError: (error: string) => void;
    countries: CountryOption[];
}> = ({ currentTemplate, setCurrentTemplate, templateNameError, setTemplateNameError, nameRegex, isEditing: _isEditing, onSaveError: _onSaveError, countries }) => {
    const [textValue, setTextValue] = useState<string>(String(currentTemplate?.templateContent || ''));
    const [selection, setSelection] = useState<{ start: number; end: number } | null>(null);
    const [showPreview, setShowPreview] = useState(false);

    useEffect(() => {
        setTextValue(String(currentTemplate?.templateContent || ''));
    }, [currentTemplate?.templateContent]);

    const applyFormatting = (format: 'bold' | 'italic' | 'strikethrough') => {
        if (!selection || selection.start === selection.end) return;
        const before = textValue.substring(0, selection.start);
        const selected = textValue.substring(selection.start, selection.end);
        const after = textValue.substring(selection.end);
        const wrapped = format === 'bold' ? `*${selected}*` : format === 'italic' ? `_${selected}_` : `~${selected}~`;
        const newText = before + wrapped + after;
        setTextValue(newText);
        setCurrentTemplate(prev => prev ? { ...prev, templateContent: newText } : null);
    };

    const formatWhatsAppMessage = (text: string) =>
        (text || '')
            .replace(/\*([^*]+)\*/g, '<strong>$1</strong>')
            .replace(/_([^_]+)_/g, '<em>$1</em>')
            .replace(/~([^~]+)~/g, '<del>$1</del>')
            .replace(/```([^`]+)```/g, '<code>$1</code>')
            .replace(/^> (.+)$/gm, '<blockquote>$1</blockquote>')
            .replace(/\n/g, '<br>');

    return (
        <Box sx={{ mt: 2 }}>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr 1fr' }, gap: 2 }}>
                <TextField
                    size="small"
                    label="Template Name"
                    fullWidth
                    value={stripSystemPrefix(currentTemplate?.templateName)}
                    onChange={e => {
                        const v = e.target.value;
                        if (!nameRegex.test(v)) setTemplateNameError('Only alphabets, numbers, underscore, and hyphen are allowed.');
                        else setTemplateNameError('');
                        setCurrentTemplate(prev => prev ? { ...prev, templateName: v } : null);
                    }}
                    error={!!templateNameError}
                    helperText={templateNameError}
                />
                <TextField size="small" label="Template Type" value="WHATSAPP" fullWidth disabled />
                <FormControl fullWidth>
                    <InputLabel>Access Level</InputLabel>
                    <Select size="small" label="Access Level" value={currentTemplate?.accessLevel || ''}
                        onChange={e => setCurrentTemplate(prev => prev ? { ...prev, accessLevel: e.target.value } : null)}>
                        <MenuItem value="public">Public</MenuItem>
                        <MenuItem value="private">Private</MenuItem>
                    </Select>
                </FormControl>
            </Box>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2, mt: 2 }}>
                <TextField
                    select
                    size="small"
                    label="Category"
                    value={currentTemplate?.category || ''}
                    onChange={e => setCurrentTemplate(prev => prev ? { ...prev, category: e.target.value } : null)}
                    fullWidth
                    SelectProps={{
                        displayEmpty: true,
                        renderValue: (v) => v ? (TEMPLATE_CATEGORIES.find(c => c.value === v)?.label ?? String(v)) : 'Select category',
                        MenuProps: { PaperProps: { sx: { '& .MuiMenuItem-root': { py: 1 } } } },
                    }}
                    InputLabelProps={{ shrink: true }}
                >
                    {TEMPLATE_CATEGORIES.map(cat => (
                        <MenuItem key={cat.value} value={cat.value}>{cat.label}</MenuItem>
                    ))}
                </TextField>
                <Autocomplete
                    size="small"
                    options={countries}
                    getOptionLabel={(option) => `${option.label} (${option.dialCode})`}
                    value={countries.find(c => c.code === currentTemplate?.country) || null}
                    onChange={(_e, newValue) => setCurrentTemplate(prev => prev ? { ...prev, country: newValue?.code || '' } : null)}
                    renderInput={(params) => <TextField {...params} label="Country" />}
                    isOptionEqualToValue={(option, value) => option.code === value.code}
                    fullWidth
                />
            </Box>
            <Autocomplete
                multiple
                freeSolo
                size="small"
                options={[]}
                value={currentTemplate?.keywords || []}
                onChange={(_e, newValue) => setCurrentTemplate(prev => prev ? { ...prev, keywords: newValue as string[] } : null)}
                renderTags={(value, getTagProps) =>
                    value.map((option, index) => (
                        <Chip size="small" label={option} {...getTagProps({ index })} key={index} />
                    ))
                }
                renderInput={(params) => <TextField {...params} label="Keywords" placeholder="Type and press Enter" />}
                sx={{ mt: 2 }}
            />

            {/* Content area */}
            <Box sx={{ mt: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                    <Typography variant="subtitle2">Template Content *</Typography>
                    <Button size="small" variant={showPreview ? "contained" : "outlined"}
                        startIcon={showPreview ? <Edit /> : <Visibility />}
                        onClick={() => setShowPreview(!showPreview)}
                        sx={{
                            flexShrink: 0,
                            backgroundColor: showPreview ? '#00a884' : 'transparent',
                            color: showPreview ? '#ffffff' : '#00a884',
                            borderColor: '#00a884',
                            '&:hover': { backgroundColor: showPreview ? '#008f72' : 'rgba(0, 168, 132, 0.1)' }
                        }}
                    >
                        {showPreview ? 'Edit' : 'Preview'}
                    </Button>
                </Box>
                {showPreview ? (
                    <Box sx={{
                        border: theme => `1px solid ${theme.palette.mode === 'dark' ? theme.palette.grey[700] : theme.palette.grey[300]}`,
                        borderRadius: '8px', padding: '16px',
                        background: theme => theme.palette.mode === 'dark' ? theme.palette.grey[900] : '#f0f2f5',
                        minHeight: '200px'
                    }}>
                        <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: 'block' }}>WhatsApp Message Preview</Typography>
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', flexDirection: 'column', alignItems: 'flex-end' }}>
                            <Box sx={{
                                maxWidth: '70%', background: '#00a884', color: '#ffffff',
                                padding: '8px 12px', borderRadius: '18px 18px 4px 18px',
                                wordWrap: 'break-word',
                                '& strong': { fontWeight: 'bold' }, '& em': { fontStyle: 'italic' },
                                '& del': { textDecoration: 'line-through' },
                                '& code': { fontFamily: 'monospace', backgroundColor: 'rgba(255,255,255,0.2)', padding: '2px 4px', borderRadius: '4px' },
                                '& blockquote': { borderLeft: '3px solid rgba(255,255,255,0.5)', paddingLeft: '8px', margin: '4px 0' }
                            }}
                            dangerouslySetInnerHTML={{ __html: formatWhatsAppMessage(textValue) }}
                            />
                        </Box>
                    </Box>
                ) : (
                    <Box sx={{
                        border: theme => `1px solid ${theme.palette.mode === 'dark' ? theme.palette.grey[700] : theme.palette.grey[300]}`,
                        borderRadius: '8px', overflow: 'hidden', width: '100%'
                    }}>
                        <Box sx={{
                            background: theme => theme.palette.mode === 'dark' ? theme.palette.grey[800] : '#f0f2f5',
                            borderBottom: theme => `1px solid ${theme.palette.mode === 'dark' ? theme.palette.grey[700] : theme.palette.grey[300]}`,
                            padding: '8px 12px', display: 'flex', gap: '4px'
                        }}>
                            <ToggleButtonGroup size="small" sx={{
                                '& .MuiToggleButton-root': { border: 'none', color: theme => theme.palette.mode === 'dark' ? theme.palette.grey[400] : '#54656f' }
                            }}>
                                <ToggleButton value="bold" onClick={() => applyFormatting('bold')} sx={{ '&:hover': { color: '#00a884' } }}><FormatBold fontSize="small" /></ToggleButton>
                                <ToggleButton value="italic" onClick={() => applyFormatting('italic')} sx={{ '&:hover': { color: '#00a884' } }}><FormatItalic fontSize="small" /></ToggleButton>
                                <ToggleButton value="strikethrough" onClick={() => applyFormatting('strikethrough')} sx={{ '&:hover': { color: '#00a884' } }}><FormatStrikethrough fontSize="small" /></ToggleButton>
                            </ToggleButtonGroup>
                        </Box>
                        <TextField
                            multiline minRows={6} maxRows={10}
                            value={textValue}
                            onChange={e => {
                                setTextValue(e.target.value);
                                setCurrentTemplate(prev => prev ? { ...prev, templateContent: e.target.value } : null);
                            }}
                            onSelect={e => {
                                const t = e.target as HTMLTextAreaElement;
                                setSelection({ start: t.selectionStart, end: t.selectionEnd });
                            }}
                            variant="standard" fullWidth
                            InputProps={{ disableUnderline: true, style: { padding: '12px 16px', fontSize: '14px', lineHeight: '1.4' } }}
                            sx={{
                                '& .MuiInputBase-root': { minHeight: '150px', color: theme => theme.palette.text.primary, backgroundColor: theme => theme.palette.background.paper },
                                width: '100%'
                            }}
                        />
                    </Box>
                )}
                <Box sx={{
                    mt: 2,
                    p: 1.5,
                    borderRadius: 1,
                    backgroundColor: theme => theme.palette.mode === 'dark' ? 'rgba(0, 168, 132, 0.15)' : 'rgba(0, 168, 132, 0.08)',
                    border: theme => `1px solid ${theme.palette.mode === 'dark' ? 'rgba(0, 168, 132, 0.3)' : 'rgba(0, 168, 132, 0.2)'}`,
                }}>
                    <Typography variant="caption" color="text.secondary">
                        To add variables, use double curly braces with consecutive numbers: {'{{1}}'}, {'{{2}}'}, {'{{3}}'}, etc. Example: &quot;Your OTP is {'{{1}}'}. Contact security at {'{{2}}'}. Thanks {'{{3}}'}&quot;
                    </Typography>
                </Box>
            </Box>
        </Box>
    );
};

/* ───────── SMS System Editor ───────── */

const SmsSystemEditor: React.FC<{
    dialogOpen: boolean;
    currentTemplate: Partial<Template> | null;
    setCurrentTemplate: React.Dispatch<React.SetStateAction<Partial<Template> | null>>;
    templateNameError: string;
    setTemplateNameError: (e: string) => void;
    nameRegex: RegExp;
    countries: CountryOption[];
}> = ({ dialogOpen, currentTemplate, setCurrentTemplate, templateNameError, setTemplateNameError, nameRegex, countries }) => {
    const initialPlain = String(currentTemplate?.templateContent || '');
    const [textValue, setTextValue] = useState<string>(initialPlain);
    const [editorHtml, setEditorHtml] = useState<string>(() => plainTextToQuillHtml(initialPlain));
    const quillRef = useRef<ReactQuill | null>(null);
    const [emojiAnchorEl, setEmojiAnchorEl] = useState<HTMLElement | null>(null);

    useEffect(() => {
        if (!dialogOpen) return;
        const plain = String(currentTemplate?.templateContent ?? '');
        setTextValue(plain);
        setEditorHtml(plainTextToQuillHtml(plain));
    }, [dialogOpen, currentTemplate?._id]);

    const smsSegmentInfo = useMemo(() => getSmsSegmentInfo(textValue), [textValue]);

    const handleSmsQuillChange = (html: string, _delta: unknown, _source: string, editor: { getText: () => string }) => {
        setEditorHtml(html);
        let plain = editor.getText();
        if (plain.endsWith('\n')) plain = plain.slice(0, -1);
        setTextValue(plain);
        setCurrentTemplate(prev => (prev ? { ...prev, templateContent: plain } : null));
    };

    const insertSmsEmoji = (emoji: string) => {
        const quill = quillRef.current?.getEditor?.();
        if (quill) {
            const range = quill.getSelection(true);
            let index = Math.max(0, quill.getLength() - 1);
            if (range && range.index != null) index = range.index;
            quill.insertText(index, emoji, 'user');
            const nextLen = emoji.length;
            quill.setSelection(index + nextLen, 0, 'silent');
        } else {
            const next = textValue + emoji;
            setTextValue(next);
            setEditorHtml(plainTextToQuillHtml(next));
            setCurrentTemplate(prev => (prev ? { ...prev, templateContent: next } : null));
        }
        setEmojiAnchorEl(null);
    };

    return (
        <Box sx={{ mt: 2 }}>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr 1fr 1fr' }, gap: 2 }}>
                <TextField size="small" label="Template Name" fullWidth
                    value={stripSystemPrefix(currentTemplate?.templateName)}
                    onChange={e => {
                        const v = e.target.value;
                        if (!nameRegex.test(v)) setTemplateNameError('Only alphabets, numbers, underscore, and hyphen are allowed.');
                        else setTemplateNameError('');
                        setCurrentTemplate(prev => prev ? { ...prev, templateName: v } : null);
                    }}
                    error={!!templateNameError} helperText={templateNameError}
                />
                <TextField size="small" label="Template ID" fullWidth
                    placeholder="Optional - e.g. DLT template ID"
                    value={currentTemplate?.templateId ?? ''}
                    onChange={e => setCurrentTemplate(prev => prev ? { ...prev, templateId: e.target.value.trim() || undefined } : null)}
                />
                <TextField size="small" label="Template Type" value="SMS" fullWidth disabled />
                <FormControl fullWidth>
                    <InputLabel>Access Level</InputLabel>
                    <Select size="small" label="Access Level" value={currentTemplate?.accessLevel || ''}
                        onChange={e => setCurrentTemplate(prev => prev ? { ...prev, accessLevel: e.target.value } : null)}>
                        <MenuItem value="public">Public</MenuItem>
                        <MenuItem value="private">Private</MenuItem>
                    </Select>
                </FormControl>
            </Box>
            <Box sx={{ mt: 2 }}>
                <TextField
                    size="small"
                    label="Sender Header Name"
                    placeholder="Optional"
                    fullWidth
                    value={currentTemplate?.senderHeaderName ?? ''}
                    onChange={e => {
                        const filtered = e.target.value.replace(/[^a-zA-Z0-9]/g, '');
                        setCurrentTemplate(prev =>
                            prev ? { ...prev, senderHeaderName: filtered || undefined } : null
                        );
                    }}
                    helperText="Alphanumeric characters only (optional)."
                    inputProps={{ inputMode: 'text', autoComplete: 'off' }}
                />
            </Box>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2, mt: 2 }}>
                <TextField
                    select
                    size="small"
                    label="Category"
                    value={currentTemplate?.category || ''}
                    onChange={e => setCurrentTemplate(prev => prev ? { ...prev, category: e.target.value } : null)}
                    fullWidth
                    SelectProps={{
                        displayEmpty: true,
                        renderValue: (v) => v ? (TEMPLATE_CATEGORIES.find(c => c.value === v)?.label ?? String(v)) : 'Select category',
                        MenuProps: { PaperProps: { sx: { '& .MuiMenuItem-root': { py: 1 } } } },
                    }}
                    InputLabelProps={{ shrink: true }}
                >
                    {TEMPLATE_CATEGORIES.map(cat => (
                        <MenuItem key={cat.value} value={cat.value}>{cat.label}</MenuItem>
                    ))}
                </TextField>
                <Autocomplete
                    size="small"
                    options={countries}
                    getOptionLabel={(option) => `${option.label} (${option.dialCode})`}
                    value={countries.find(c => c.code === currentTemplate?.country) || null}
                    onChange={(_e, newValue) => setCurrentTemplate(prev => prev ? { ...prev, country: newValue?.code || '' } : null)}
                    renderInput={(params) => <TextField {...params} label="Country" />}
                    isOptionEqualToValue={(option, value) => option.code === value.code}
                    fullWidth
                />
            </Box>
            <Autocomplete
                multiple
                freeSolo
                size="small"
                options={[]}
                value={currentTemplate?.keywords || []}
                onChange={(_e, newValue) => setCurrentTemplate(prev => prev ? { ...prev, keywords: newValue as string[] } : null)}
                renderTags={(value, getTagProps) =>
                    value.map((option, index) => (
                        <Chip size="small" label={option} {...getTagProps({ index })} key={index} />
                    ))
                }
                renderInput={(params) => <TextField {...params} label="Keywords" placeholder="Type and press Enter" />}
                sx={{ mt: 2 }}
            />
            <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>Template Content *</Typography>
                <Box
                    sx={{
                        border: '1px solid',
                        borderColor: 'divider',
                        borderRadius: 1,
                        overflow: 'hidden',
                        '& .ql-container.ql-snow': {
                            border: 'none',
                            borderTop: '1px solid',
                            borderColor: 'divider',
                            fontSize: '0.95rem',
                            minHeight: 140,
                        },
                        '& .ql-editor': {
                            minHeight: 120,
                        },
                    }}
                >
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            gap: 1,
                            flexWrap: 'wrap',
                            px: 0.5,
                            py: 0.25,
                            borderBottom: '1px solid',
                            borderColor: 'divider',
                            backgroundColor: theme => (theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)'),
                        }}
                    >
                        <Tooltip title="Insert emoji (uses UCS-2 encoding for the SMS)">
                            <IconButton
                                size="small"
                                color="secondary"
                                aria-label="Insert emoji"
                                onClick={e => setEmojiAnchorEl(emojiAnchorEl ? null : e.currentTarget)}
                            >
                                <EmojiEmotions fontSize="small" />
                            </IconButton>
                        </Tooltip>
                        <Chip
                            icon={<LocalOffer sx={{ fontSize: 18 }} />}
                            label={`${smsSegmentInfo.segments} SMS segment${smsSegmentInfo.segments === 1 ? '' : 's'} (${smsSegmentInfo.encoding})`}
                            size="small"
                            sx={{
                                ml: 'auto',
                                fontWeight: 800,
                                color: 'success.main',
                                bgcolor: theme => theme.palette.mode === 'dark' ? 'rgba(0,0,0,0.45)' : 'rgba(255,255,255,0.96)',
                                border: '1px solid',
                                borderColor: 'success.main',
                                height: 28,
                                maxWidth: { xs: '100%', sm: 'min(100%, 340px)' },
                                boxShadow: '0 1px 3px rgba(0,0,0,0.12)',
                                '& .MuiChip-icon': {
                                    ml: 0.5,
                                    color: 'success.main',
                                },
                                '& .MuiChip-label': {
                                    px: 0.75,
                                    fontSize: '0.75rem',
                                    whiteSpace: 'normal',
                                    textAlign: 'right',
                                    lineHeight: 1.25,
                                },
                            }}
                        />
                    </Box>
                    <Popover
                        open={Boolean(emojiAnchorEl)}
                        anchorEl={emojiAnchorEl}
                        onClose={() => setEmojiAnchorEl(null)}
                        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                        slotProps={{ paper: { sx: { p: 1, maxWidth: 320, maxHeight: 280, overflow: 'auto' } } }}
                    >
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, justifyContent: 'flex-start' }}>
                            {SMS_QUICK_EMOJIS.map((emoji, idx) => (
                                <IconButton
                                    key={`sms-emoji-${idx}`}
                                    size="small"
                                    onClick={() => insertSmsEmoji(emoji)}
                                    sx={{
                                        fontSize: '1.35rem',
                                        width: 36,
                                        height: 36,
                                        p: 0,
                                        borderRadius: 1,
                                        '&:hover': { backgroundColor: 'action.hover' },
                                    }}
                                    aria-label={`Insert ${emoji}`}
                                >
                                    <span style={{ lineHeight: 1 }}>{emoji}</span>
                                </IconButton>
                            ))}
                        </Box>
                    </Popover>
                    <ReactQuill
                        ref={quillRef}
                        theme="snow"
                        value={editorHtml}
                        onChange={handleSmsQuillChange}
                        modules={SMS_QUILL_MODULES}
                        formats={[]}
                    />
                </Box>
                <Box sx={{
                    mt: 2,
                    p: 1.5,
                    borderRadius: 1,
                    backgroundColor: theme => theme.palette.mode === 'dark' ? 'rgba(156, 39, 176, 0.15)' : 'rgba(156, 39, 176, 0.08)',
                    border: theme => `1px solid ${theme.palette.mode === 'dark' ? 'rgba(156, 39, 176, 0.3)' : 'rgba(156, 39, 176, 0.2)'}`,
                }}>
                    <Typography variant="caption" color="text.secondary" component="div">
                        To add variables, use double curly braces with consecutive numbers: {'{{1}}'}, {'{{2}}'}, {'{{3}}'}, etc. Example: &quot;Your OTP is {'{{1}}'}. Contact security at {'{{2}}'}. Thanks {'{{3}}'}&quot;
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 0.75, display: 'block' }}>
                        Only plain text is sent as SMS; use the emoji button in the toolbar to insert symbols. Emojis and other non–GSM-7 characters use <strong>UCS-2</strong> encoding (segment count updates live).
                    </Typography>
                </Box>
            </Box>
        </Box>
    );
};

/* ───────── Telegram System Editor ───────── */

const TELEGRAM_ACCENT = '#0088cc';
const TELEGRAM_ACCENT_DARK = '#006daa';

type TelegramFormat = 'bold' | 'italic' | 'underline' | 'strikethrough' | 'monospace' | 'codeblock' | 'spoiler' | 'link';

const TELEGRAM_FORMAT_MAP: Record<TelegramFormat, { prefix: string; suffix: string }> = {
    bold:          { prefix: '*',   suffix: '*' },
    italic:        { prefix: '_',   suffix: '_' },
    underline:     { prefix: '__',  suffix: '__' },
    strikethrough: { prefix: '~',   suffix: '~' },
    monospace:     { prefix: '`',   suffix: '`' },
    codeblock:     { prefix: '```\n', suffix: '\n```' },
    spoiler:       { prefix: '||',  suffix: '||' },
    link:          { prefix: '[',   suffix: '](url)' },
};

const formatTelegramMessage = (text: string) =>
    (text || '')
        .replace(/```([\s\S]*?)```/g, '<pre style="background:rgba(0,136,204,0.1);padding:8px 12px;border-radius:4px;font-family:monospace;white-space:pre-wrap;margin:4px 0">$1</pre>')
        .replace(/\|\|([^|]+)\|\|/g, '<span style="background:currentColor;border-radius:2px;cursor:pointer" title="Spoiler (hover to reveal)">$1</span>')
        .replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" style="color:#0088cc;text-decoration:underline">$1</a>')
        .replace(/\*([^*]+)\*/g, '<strong>$1</strong>')
        .replace(/__([^_]+)__/g, '<u>$1</u>')
        .replace(/_([^_]+)_/g, '<em>$1</em>')
        .replace(/~([^~]+)~/g, '<del>$1</del>')
        .replace(/`([^`]+)`/g, '<code style="font-family:monospace;background:rgba(0,136,204,0.1);padding:2px 4px;border-radius:3px">$1</code>')
        .replace(/\n/g, '<br>');

const TelegramSystemEditor: React.FC<{
    currentTemplate: Partial<Template> | null;
    setCurrentTemplate: React.Dispatch<React.SetStateAction<Partial<Template> | null>>;
    templateNameError: string;
    setTemplateNameError: (e: string) => void;
    nameRegex: RegExp;
    isEditing: boolean;
    onSaveError: (error: string) => void;
    countries: CountryOption[];
}> = ({ currentTemplate, setCurrentTemplate, templateNameError, setTemplateNameError, nameRegex, isEditing: _isEditing, onSaveError: _onSaveError, countries }) => {
    const [textValue, setTextValue] = useState<string>(String(currentTemplate?.templateContent || ''));
    const [selection, setSelection] = useState<{ start: number; end: number } | null>(null);
    const [showPreview, setShowPreview] = useState(false);
    const textFieldRef = React.useRef<HTMLTextAreaElement | null>(null);
    const [mediaAttachments, setMediaAttachments] = useState<any[]>(currentTemplate?.mediaAttachments || []);
    const [uploadingMedia, setUploadingMedia] = useState(false);
    const [uploadError, setUploadError] = useState('');

    useEffect(() => {
        setTextValue(String(currentTemplate?.templateContent || ''));
    }, [currentTemplate?.templateContent]);

    useEffect(() => {
        setMediaAttachments(currentTemplate?.mediaAttachments || []);
    }, [currentTemplate?._id]);

    const MAX_FILE_SIZE = 20 * 1024 * 1024;

    const handleMediaUpload = (file: File) => {
        if (file.size > MAX_FILE_SIZE) {
            setUploadError(`File size (${(file.size / 1024 / 1024).toFixed(1)}MB) exceeds the 20MB limit.`);
            return;
        }
        setUploadingMedia(true);
        setUploadError('');
        const reader = new FileReader();
        reader.onload = () => {
            const base64 = reader.result as string;
            const mimeType = file.type || 'application/octet-stream';
            let type: 'image' | 'video' | 'document' | 'audio' = 'document';
            if (mimeType.startsWith('image/')) type = 'image';
            else if (mimeType.startsWith('video/')) type = 'video';
            else if (mimeType.startsWith('audio/')) type = 'audio';

            const newAttachment = {
                type,
                filename: `${Date.now()}-${file.name}`,
                originalName: file.name,
                mimeType,
                size: file.size,
                data: base64,
                isNew: true,
                localUrl: URL.createObjectURL(file),
            };
            const updated = [...mediaAttachments, newAttachment];
            setMediaAttachments(updated);
            setCurrentTemplate(prev => prev ? { ...prev, mediaAttachments: updated } : null);
            setUploadingMedia(false);
        };
        reader.onerror = () => {
            setUploadError('Failed to read file.');
            setUploadingMedia(false);
        };
        reader.readAsDataURL(file);
    };

    const handleMediaDelete = (filename: string) => {
        const updated = mediaAttachments.map(att =>
            att.filename === filename ? { ...att, isDeleted: true } : att
        );
        setMediaAttachments(updated);
        setCurrentTemplate(prev => prev ? { ...prev, mediaAttachments: updated } : null);
    };

    const activeAttachments = mediaAttachments.filter((a: any) => a && a.mimeType && !a.isDeleted);

    const applyFormatting = (format: TelegramFormat) => {
        const { prefix, suffix } = TELEGRAM_FORMAT_MAP[format];
        const el = textFieldRef.current;
        const start = el ? el.selectionStart : (selection?.start ?? textValue.length);
        const end = el ? el.selectionEnd : (selection?.end ?? textValue.length);
        const hasSelection = start !== end;

        const before = textValue.substring(0, start);
        const selected = hasSelection ? textValue.substring(start, end) : '';
        const after = textValue.substring(end);

        let newText: string;
        let cursorPos: number;

        if (hasSelection) {
            newText = before + prefix + selected + suffix + after;
            cursorPos = start + prefix.length + selected.length + suffix.length;
        } else {
            const placeholder = format === 'link' ? 'text' : format === 'codeblock' ? 'code' : 'text';
            newText = before + prefix + placeholder + suffix + after;
            cursorPos = start + prefix.length;
        }

        setTextValue(newText);
        setCurrentTemplate(prev => prev ? { ...prev, templateContent: newText } : null);

        requestAnimationFrame(() => {
            if (el) {
                el.focus();
                if (!hasSelection) {
                    const placeholderLen = format === 'link' ? 4 : format === 'codeblock' ? 4 : 4;
                    el.setSelectionRange(cursorPos, cursorPos + placeholderLen);
                } else {
                    el.setSelectionRange(cursorPos, cursorPos);
                }
            }
        });
    };

    return (
        <Box sx={{ mt: 2 }}>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr 1fr' }, gap: 2 }}>
                <TextField
                    size="small"
                    label="Template Name"
                    fullWidth
                    value={stripSystemPrefix(currentTemplate?.templateName)}
                    onChange={e => {
                        const v = e.target.value;
                        if (!nameRegex.test(v)) setTemplateNameError('Only alphabets, numbers, underscore, and hyphen are allowed.');
                        else setTemplateNameError('');
                        setCurrentTemplate(prev => prev ? { ...prev, templateName: v } : null);
                    }}
                    error={!!templateNameError}
                    helperText={templateNameError}
                />
                <TextField size="small" label="Template Type" value="TELEGRAM" fullWidth disabled />
                <FormControl fullWidth>
                    <InputLabel>Access Level</InputLabel>
                    <Select size="small" label="Access Level" value={currentTemplate?.accessLevel || ''}
                        onChange={e => setCurrentTemplate(prev => prev ? { ...prev, accessLevel: e.target.value } : null)}>
                        <MenuItem value="public">Public</MenuItem>
                        <MenuItem value="private">Private</MenuItem>
                    </Select>
                </FormControl>
            </Box>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2, mt: 2 }}>
                <TextField
                    select
                    size="small"
                    label="Category"
                    value={currentTemplate?.category || ''}
                    onChange={e => setCurrentTemplate(prev => prev ? { ...prev, category: e.target.value } : null)}
                    fullWidth
                    SelectProps={{
                        displayEmpty: true,
                        renderValue: (v) => v ? (TEMPLATE_CATEGORIES.find(c => c.value === v)?.label ?? String(v)) : 'Select category',
                        MenuProps: { PaperProps: { sx: { '& .MuiMenuItem-root': { py: 1 } } } },
                    }}
                    InputLabelProps={{ shrink: true }}
                >
                    {TEMPLATE_CATEGORIES.map(cat => (
                        <MenuItem key={cat.value} value={cat.value}>{cat.label}</MenuItem>
                    ))}
                </TextField>
                <Autocomplete
                    size="small"
                    options={countries}
                    getOptionLabel={(option) => `${option.label} (${option.dialCode})`}
                    value={countries.find(c => c.code === currentTemplate?.country) || null}
                    onChange={(_e, newValue) => setCurrentTemplate(prev => prev ? { ...prev, country: newValue?.code || '' } : null)}
                    renderInput={(params) => <TextField {...params} label="Country" />}
                    isOptionEqualToValue={(option, value) => option.code === value.code}
                    fullWidth
                />
            </Box>
            <Autocomplete
                multiple
                freeSolo
                size="small"
                options={[]}
                value={currentTemplate?.keywords || []}
                onChange={(_e, newValue) => setCurrentTemplate(prev => prev ? { ...prev, keywords: newValue as string[] } : null)}
                renderTags={(value, getTagProps) =>
                    value.map((option, index) => (
                        <Chip size="small" label={option} {...getTagProps({ index })} key={index} />
                    ))
                }
                renderInput={(params) => <TextField {...params} label="Keywords" placeholder="Type and press Enter" />}
                sx={{ mt: 2 }}
            />

            {/* Content area */}
            <Box sx={{ mt: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                    <Typography variant="subtitle2">Template Content *</Typography>
                    <Button size="small" variant={showPreview ? "contained" : "outlined"}
                        startIcon={showPreview ? <Edit /> : <Visibility />}
                        onClick={() => setShowPreview(!showPreview)}
                        sx={{
                            flexShrink: 0,
                            backgroundColor: showPreview ? TELEGRAM_ACCENT : 'transparent',
                            color: showPreview ? '#ffffff' : TELEGRAM_ACCENT,
                            borderColor: TELEGRAM_ACCENT,
                            '&:hover': { backgroundColor: showPreview ? TELEGRAM_ACCENT_DARK : 'rgba(0, 136, 204, 0.1)' }
                        }}
                    >
                        {showPreview ? 'Edit' : 'Preview'}
                    </Button>
                </Box>
                {showPreview ? (
                    <Box sx={{
                        border: theme => `1px solid ${theme.palette.mode === 'dark' ? theme.palette.grey[700] : theme.palette.grey[300]}`,
                        borderRadius: '8px', padding: '16px',
                        background: theme => theme.palette.mode === 'dark' ? theme.palette.grey[900] : '#f0f2f5',
                        minHeight: '200px'
                    }}>
                        <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: 'block' }}>Telegram Message Preview</Typography>
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                            <Box sx={{
                                maxWidth: '80%', background: theme => theme.palette.mode === 'dark' ? '#1e3a5f' : '#e3f2fd',
                                color: theme => theme.palette.text.primary,
                                padding: '8px 12px', borderRadius: '4px 18px 18px 18px',
                                wordWrap: 'break-word',
                                '& strong': { fontWeight: 'bold' }, '& em': { fontStyle: 'italic' },
                                '& u': { textDecoration: 'underline' },
                                '& del': { textDecoration: 'line-through' },
                                '& pre': { whiteSpace: 'pre-wrap' },
                            }}
                            dangerouslySetInnerHTML={{ __html: formatTelegramMessage(textValue) }}
                            />
                        </Box>
                    </Box>
                ) : (
                    <Box sx={{
                        border: theme => `1px solid ${theme.palette.mode === 'dark' ? theme.palette.grey[700] : theme.palette.grey[300]}`,
                        borderRadius: '8px', overflow: 'hidden', width: '100%'
                    }}>
                        <Box sx={{
                            background: theme => theme.palette.mode === 'dark' ? theme.palette.grey[800] : '#f0f2f5',
                            borderBottom: theme => `1px solid ${theme.palette.mode === 'dark' ? theme.palette.grey[700] : theme.palette.grey[300]}`,
                            padding: '6px 12px', display: 'flex', gap: '2px', flexWrap: 'wrap', alignItems: 'center'
                        }}>
                            <ToggleButtonGroup size="small" sx={{
                                '& .MuiToggleButton-root': { border: 'none', color: theme => theme.palette.mode === 'dark' ? theme.palette.grey[400] : '#54656f', px: 1 }
                            }}>
                                <ToggleButton value="bold" onClick={() => applyFormatting('bold')}
                                    sx={{ '&:hover': { color: TELEGRAM_ACCENT } }}>
                                    <FormatBold fontSize="small" />
                                </ToggleButton>
                                <ToggleButton value="italic" onClick={() => applyFormatting('italic')}
                                    sx={{ '&:hover': { color: TELEGRAM_ACCENT } }}>
                                    <FormatItalic fontSize="small" />
                                </ToggleButton>
                                <ToggleButton value="underline" onClick={() => applyFormatting('underline')}
                                    sx={{ '&:hover': { color: TELEGRAM_ACCENT } }}>
                                    <FormatUnderlined fontSize="small" />
                                </ToggleButton>
                                <ToggleButton value="strikethrough" onClick={() => applyFormatting('strikethrough')}
                                    sx={{ '&:hover': { color: TELEGRAM_ACCENT } }}>
                                    <FormatStrikethrough fontSize="small" />
                                </ToggleButton>
                            </ToggleButtonGroup>
                            <Box sx={{ width: '1px', height: 20, backgroundColor: 'divider', mx: 0.5 }} />
                            <ToggleButtonGroup size="small" sx={{
                                '& .MuiToggleButton-root': { border: 'none', color: theme => theme.palette.mode === 'dark' ? theme.palette.grey[400] : '#54656f', px: 1 }
                            }}>
                                <ToggleButton value="monospace" onClick={() => applyFormatting('monospace')}
                                    sx={{ '&:hover': { color: TELEGRAM_ACCENT } }}>
                                    <Code fontSize="small" />
                                </ToggleButton>
                                <ToggleButton value="codeblock" onClick={() => applyFormatting('codeblock')}
                                    sx={{ '&:hover': { color: TELEGRAM_ACCENT } }}>
                                    <DataObject fontSize="small" />
                                </ToggleButton>
                            </ToggleButtonGroup>
                            <Box sx={{ width: '1px', height: 20, backgroundColor: 'divider', mx: 0.5 }} />
                            <ToggleButtonGroup size="small" sx={{
                                '& .MuiToggleButton-root': { border: 'none', color: theme => theme.palette.mode === 'dark' ? theme.palette.grey[400] : '#54656f', px: 1 }
                            }}>
                                <ToggleButton value="spoiler" onClick={() => applyFormatting('spoiler')}
                                    sx={{ '&:hover': { color: TELEGRAM_ACCENT } }}>
                                    <VisibilityOff fontSize="small" />
                                </ToggleButton>
                                <ToggleButton value="link" onClick={() => applyFormatting('link')}
                                    sx={{ '&:hover': { color: TELEGRAM_ACCENT } }}>
                                    <LinkIcon fontSize="small" />
                                </ToggleButton>
                            </ToggleButtonGroup>
                        </Box>
                        <TextField
                            multiline minRows={6} maxRows={10}
                            value={textValue}
                            onChange={e => {
                                setTextValue(e.target.value);
                                setCurrentTemplate(prev => prev ? { ...prev, templateContent: e.target.value } : null);
                            }}
                            onSelect={e => {
                                const t = e.target as HTMLTextAreaElement;
                                setSelection({ start: t.selectionStart, end: t.selectionEnd });
                            }}
                            inputRef={textFieldRef}
                            variant="standard" fullWidth
                            InputProps={{ disableUnderline: true, style: { padding: '12px 16px', fontSize: '14px', lineHeight: '1.4' } }}
                            sx={{
                                '& .MuiInputBase-root': { minHeight: '150px', color: theme => theme.palette.text.primary, backgroundColor: theme => theme.palette.background.paper },
                                width: '100%'
                            }}
                        />
                    </Box>
                )}
                <Box sx={{
                    mt: 2,
                    p: 1.5,
                    borderRadius: 1,
                    backgroundColor: theme => theme.palette.mode === 'dark' ? 'rgba(0, 136, 204, 0.15)' : 'rgba(0, 136, 204, 0.08)',
                    border: theme => `1px solid ${theme.palette.mode === 'dark' ? 'rgba(0, 136, 204, 0.3)' : 'rgba(0, 136, 204, 0.2)'}`,
                }}>
                    <Typography variant="caption" color="text.secondary" component="div">
                        <strong>Formatting:</strong> *bold* &nbsp; _italic_ &nbsp; __underline__ &nbsp; ~strikethrough~ &nbsp; `monospace` &nbsp; ```code block``` &nbsp; ||spoiler|| &nbsp; [text](url)
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                        <strong>Variables:</strong> Use double curly braces with consecutive numbers: {'{{1}}'}, {'{{2}}'}, {'{{3}}'}, etc.
                    </Typography>
                </Box>
            </Box>

            {/* Media Attachments Section */}
            <Box sx={{ mt: 3 }}>
                <Typography variant="subtitle2" gutterBottom>Media Attachments</Typography>
                <Box sx={{ mb: 2 }}>
                    <input
                        type="file"
                        accept="image/*,video/*,audio/*,.pdf,.doc,.docx,.txt,.zip,.rar"
                        onChange={e => {
                            const file = e.target.files?.[0];
                            if (file) { setUploadError(''); handleMediaUpload(file); }
                            e.target.value = '';
                        }}
                        style={{ display: 'none' }}
                        id="telegram-media-upload-input"
                        disabled={uploadingMedia}
                    />
                    <label htmlFor="telegram-media-upload-input">
                        <Button variant="outlined" component="span"
                            startIcon={uploadingMedia ? <CircularProgress size={16} /> : <AttachFile />}
                            disabled={uploadingMedia}
                            sx={{ borderColor: TELEGRAM_ACCENT, color: TELEGRAM_ACCENT, '&:hover': { borderColor: TELEGRAM_ACCENT_DARK, backgroundColor: 'rgba(0, 136, 204, 0.1)' } }}>
                            {uploadingMedia ? 'Processing...' : 'Add Media'}
                        </Button>
                    </label>
                    {uploadingMedia && <LinearProgress sx={{ mt: 1, borderRadius: 1 }} />}
                    {uploadError && (
                        <Box sx={{ mt: 1 }}>
                            <Typography variant="caption" color="error" sx={{
                                display: 'flex', alignItems: 'center', gap: 0.5,
                                backgroundColor: 'rgba(244, 67, 54, 0.1)', padding: 1, borderRadius: 1,
                                border: '1px solid rgba(244, 67, 54, 0.3)'
                            }}>
                                <ErrorOutline sx={{ fontSize: 16 }} />{uploadError}
                            </Typography>
                        </Box>
                    )}
                </Box>
                {activeAttachments.length > 0 && (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        {activeAttachments.map((attachment: any, index: number) => (
                            <Card key={index} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: '8px', overflow: 'hidden' }}>
                                {attachment.mimeType?.startsWith('image/') && attachment.localUrl && (
                                    <CardMedia component="img" height="120" image={attachment.localUrl} alt={attachment.originalName || attachment.filename} sx={{ objectFit: 'cover' }} />
                                )}
                                {attachment.mimeType?.startsWith('video/') && attachment.localUrl && (
                                    <Box sx={{ position: 'relative', height: 120 }}>
                                        <video src={attachment.localUrl} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                        <Box sx={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', width: 40, height: 40, background: 'rgba(0,0,0,0.7)', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                            <Box sx={{ width: 0, height: 0, borderTop: '8px solid transparent', borderBottom: '8px solid transparent', borderLeft: '12px solid white', ml: '2px' }} />
                                        </Box>
                                    </Box>
                                )}
                                <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        {attachment.mimeType?.startsWith('image/') && <Image sx={{ color: TELEGRAM_ACCENT, fontSize: 20 }} />}
                                        {attachment.mimeType?.startsWith('video/') && <VideoFile sx={{ color: TELEGRAM_ACCENT, fontSize: 20 }} />}
                                        {attachment.mimeType?.startsWith('audio/') && <AudioFile sx={{ color: TELEGRAM_ACCENT, fontSize: 20 }} />}
                                        {!attachment.mimeType?.startsWith('image/') && !attachment.mimeType?.startsWith('video/') && !attachment.mimeType?.startsWith('audio/') && <Description sx={{ color: TELEGRAM_ACCENT, fontSize: 20 }} />}
                                        <Box sx={{ flex: 1, minWidth: 0 }}>
                                            <Typography variant="body2" sx={{ fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                {attachment.originalName || attachment.filename}
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary">
                                                {attachment.mimeType?.split('/')[0]?.charAt(0).toUpperCase()}{attachment.mimeType?.split('/')[0]?.slice(1)} &bull; {((attachment.size || 0) / 1024 / 1024).toFixed(2)} MB
                                            </Typography>
                                        </Box>
                                        <IconButton size="small" onClick={() => handleMediaDelete(attachment.filename)} sx={{ color: 'error.main' }}>
                                            <Delete fontSize="small" />
                                        </IconButton>
                                    </Box>
                                </CardContent>
                            </Card>
                        ))}
                    </Box>
                )}
            </Box>
        </Box>
    );
};

/* ───────── WhatsApp Business System Editor ───────── */

const WA_BIZ_GREEN = '#25d366';
const WA_BIZ_GREEN_DARK = '#128c7e';

const META_TEMPLATE_CATEGORIES = [
    { value: 'MARKETING', label: 'Marketing' },
    { value: 'UTILITY', label: 'Utility' },
    { value: 'AUTHENTICATION', label: 'Authentication' },
];

const META_LANGUAGES = [
    { code: 'en', label: 'English' },
    { code: 'en_US', label: 'English (US)' },
    { code: 'en_GB', label: 'English (UK)' },
    { code: 'es', label: 'Spanish' },
    { code: 'pt_BR', label: 'Portuguese (BR)' },
    { code: 'fr', label: 'French' },
    { code: 'de', label: 'German' },
    { code: 'it', label: 'Italian' },
    { code: 'hi', label: 'Hindi' },
    { code: 'ar', label: 'Arabic' },
    { code: 'zh_CN', label: 'Chinese (CN)' },
    { code: 'ja', label: 'Japanese' },
    { code: 'ko', label: 'Korean' },
    { code: 'ru', label: 'Russian' },
    { code: 'tr', label: 'Turkish' },
    { code: 'nl', label: 'Dutch' },
    { code: 'id', label: 'Indonesian' },
    { code: 'ms', label: 'Malay' },
    { code: 'th', label: 'Thai' },
    { code: 'vi', label: 'Vietnamese' },
];

const HEADER_TYPES: { value: 'none' | 'text' | 'image' | 'video' | 'document'; label: string; icon: React.ReactNode }[] = [
    { value: 'none', label: 'None', icon: null },
    { value: 'text', label: 'Text', icon: <TextFields fontSize="small" /> },
    { value: 'image', label: 'Image', icon: <ImageOutlined fontSize="small" /> },
    { value: 'video', label: 'Video', icon: <Videocam fontSize="small" /> },
    { value: 'document', label: 'Document', icon: <InsertDriveFile fontSize="small" /> },
];

const BUTTON_TYPES: { value: 'URL' | 'PHONE_NUMBER' | 'QUICK_REPLY'; label: string; icon: React.ReactNode }[] = [
    { value: 'URL', label: 'Visit Website', icon: <OpenInNew fontSize="small" /> },
    { value: 'PHONE_NUMBER', label: 'Call Phone', icon: <Phone fontSize="small" /> },
    { value: 'QUICK_REPLY', label: 'Quick Reply', icon: <Reply fontSize="small" /> },
];

const WaBizSection: React.FC<{ title: string; number: number; children: React.ReactNode; optional?: boolean }> = ({ title, number, children, optional }) => (
    <Paper variant="outlined" sx={{ p: 2, mt: 2, borderRadius: 2, borderColor: 'divider' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
            <Box sx={{ width: 24, height: 24, borderRadius: '50%', background: WA_BIZ_GREEN, color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 700 }}>{number}</Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>{title}</Typography>
            {optional && <Chip label="Optional" size="small" variant="outlined" sx={{ fontSize: 10, height: 20 }} />}
        </Box>
        {children}
    </Paper>
);

const WhatsAppBusinessSystemEditor: React.FC<{
    currentTemplate: Partial<import("modules/Interfaces/InstanUpdates/Template").Template> | null;
    setCurrentTemplate: React.Dispatch<React.SetStateAction<Partial<import("modules/Interfaces/InstanUpdates/Template").Template> | null>>;
    templateNameError: string;
    setTemplateNameError: (e: string) => void;
    isEditing: boolean;
    onSaveError: (error: string) => void;
    countries: CountryOption[];
}> = ({ currentTemplate, setCurrentTemplate, templateNameError, setTemplateNameError, isEditing: _isEditing, onSaveError: _onSaveError, countries }) => {
    const nameRegex = /^[a-z0-9_]*$/;

    const getConfig = (): WhatsAppBusinessConfig => currentTemplate?.whatsappBusinessConfig || {
        language: 'en_US', header: { type: 'none' }, footer: '', buttons: [],
    };

    const updateConfig = (partial: Partial<WhatsAppBusinessConfig>) => {
        setCurrentTemplate(prev => prev ? {
            ...prev,
            whatsappBusinessConfig: { ...getConfig(), ...partial }
        } : null);
    };

    const [bodyValue, setBodyValue] = useState<string>(String(currentTemplate?.templateContent || ''));
    const [selection, setSelection] = useState<{ start: number; end: number } | null>(null);
    const bodyRef = React.useRef<HTMLTextAreaElement | null>(null);
    const [showPreview, setShowPreview] = useState(false);
    const [headerMediaFile, setHeaderMediaFile] = useState<File | null>(null);
    const [headerMediaPreview, setHeaderMediaPreview] = useState<string>('');

    useEffect(() => {
        setBodyValue(String(currentTemplate?.templateContent || ''));
    }, [currentTemplate?.templateContent]);

    useEffect(() => {
        return () => { if (headerMediaPreview) URL.revokeObjectURL(headerMediaPreview); };
    }, [headerMediaPreview]);

    const config = getConfig();

    const applyFormatting = (format: 'bold' | 'italic' | 'strikethrough' | 'monospace') => {
        const el = bodyRef.current;
        const start = el ? el.selectionStart : (selection?.start ?? bodyValue.length);
        const end = el ? el.selectionEnd : (selection?.end ?? bodyValue.length);
        const hasSelection = start !== end;
        const before = bodyValue.substring(0, start);
        const selectedText = hasSelection ? bodyValue.substring(start, end) : 'text';
        const after = bodyValue.substring(end);
        const wrap = format === 'bold' ? '*' : format === 'italic' ? '_' : format === 'strikethrough' ? '~' : '```';
        const newText = before + wrap + selectedText + wrap + after;
        setBodyValue(newText);
        setCurrentTemplate(prev => prev ? { ...prev, templateContent: newText } : null);
        requestAnimationFrame(() => {
            if (el) { el.focus(); const pos = start + wrap.length + selectedText.length + wrap.length; el.setSelectionRange(pos, pos); }
        });
    };

    const insertVariable = () => {
        const existing = (bodyValue.match(/\{\{(\d+)\}\}/g) || []).map(m => parseInt(m.replace(/[{}]/g, '')));
        const next = existing.length > 0 ? Math.max(...existing) + 1 : 1;
        const el = bodyRef.current;
        const pos = el ? el.selectionStart : bodyValue.length;
        const tag = `{{${next}}}`;
        const newText = bodyValue.substring(0, pos) + tag + bodyValue.substring(pos);
        setBodyValue(newText);
        setCurrentTemplate(prev => prev ? { ...prev, templateContent: newText } : null);
        requestAnimationFrame(() => { if (el) { el.focus(); const newPos = pos + tag.length; el.setSelectionRange(newPos, newPos); } });
    };

    const addButton = () => {
        if (config.buttons.length >= 3) return;
        updateConfig({ buttons: [...config.buttons, { type: 'QUICK_REPLY', text: '' }] });
    };

    const updateButton = (index: number, partial: Partial<WhatsAppBusinessButton>) => {
        const updated = config.buttons.map((btn, i) => i === index ? { ...btn, ...partial } : btn);
        updateConfig({ buttons: updated });
    };

    const removeButton = (index: number) => {
        updateConfig({ buttons: config.buttons.filter((_, i) => i !== index) });
    };

    const handleHeaderMediaChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;
        if (headerMediaPreview) URL.revokeObjectURL(headerMediaPreview);
        setHeaderMediaFile(file);
        setHeaderMediaPreview(URL.createObjectURL(file));
        const reader = new FileReader();
        reader.onload = () => {
            const base64 = reader.result as string;
            const mimeType = file.type || 'application/octet-stream';
            let mediaType: 'image' | 'video' | 'document' = 'document';
            if (mimeType.startsWith('image/')) mediaType = 'image';
            else if (mimeType.startsWith('video/')) mediaType = 'video';
            const attachment = [{
                type: mediaType, filename: `header-${Date.now()}-${file.name}`, mimeType, size: file.size,
                data: base64, isNew: true, localUrl: URL.createObjectURL(file),
            }];
            setCurrentTemplate(prev => prev ? { ...prev, mediaAttachments: attachment } : null);
        };
        reader.readAsDataURL(file);
        e.target.value = '';
    };

    const formatWhatsAppBiz = (text: string) =>
        (text || '')
            .replace(/\*([^*]+)\*/g, '<strong>$1</strong>')
            .replace(/_([^_]+)_/g, '<em>$1</em>')
            .replace(/~([^~]+)~/g, '<del>$1</del>')
            .replace(/```([^`]+)```/g, '<code style="font-family:monospace;background:rgba(0,0,0,0.06);padding:2px 4px;border-radius:3px">$1</code>')
            .replace(/\n/g, '<br>');

    const getAcceptForHeader = () => {
        if (config.header.type === 'image') return 'image/jpeg,image/png';
        if (config.header.type === 'video') return 'video/mp4';
        if (config.header.type === 'document') return '.pdf';
        return '';
    };

    return (
        <Box sx={{ mt: 2 }}>
            {/* Row 1: Name, Language, Category */}
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr 1fr' }, gap: 2 }}>
                <TextField
                    size="small" label="Template Name" fullWidth
                    value={stripSystemPrefix(currentTemplate?.templateName)}
                    onChange={e => {
                        const v = e.target.value;
                        if (!nameRegex.test(v)) setTemplateNameError('Only lowercase letters, numbers, and underscores are allowed.');
                        else setTemplateNameError('');
                        setCurrentTemplate(prev => prev ? { ...prev, templateName: v } : null);
                    }}
                    error={!!templateNameError} helperText={templateNameError || 'Lowercase letters, numbers, and underscores only'}
                    placeholder="e.g. order_confirmation"
                />
                <TextField select size="small" label="Language" fullWidth
                    value={config.language}
                    onChange={e => updateConfig({ language: e.target.value })}
                >
                    {META_LANGUAGES.map(l => <MenuItem key={l.code} value={l.code}>{l.label} ({l.code})</MenuItem>)}
                </TextField>
                <TextField select size="small" label="Category" fullWidth
                    value={currentTemplate?.category || ''}
                    onChange={e => setCurrentTemplate(prev => prev ? { ...prev, category: e.target.value } : null)}
                    SelectProps={{ displayEmpty: true, renderValue: (v) => v ? (META_TEMPLATE_CATEGORIES.find(c => c.value === v)?.label ?? String(v)) : 'Select category' }}
                    InputLabelProps={{ shrink: true }}
                >
                    {META_TEMPLATE_CATEGORIES.map(c => <MenuItem key={c.value} value={c.value}>{c.label}</MenuItem>)}
                </TextField>
            </Box>
            {/* Row 2: Country, Keywords, Access */}
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr 1fr' }, gap: 2, mt: 2 }}>
                <Autocomplete
                    size="small" options={countries}
                    getOptionLabel={(option) => `${option.label} (${option.dialCode})`}
                    value={countries.find(c => c.code === currentTemplate?.country) || null}
                    onChange={(_e, newValue) => setCurrentTemplate(prev => prev ? { ...prev, country: newValue?.code || '' } : null)}
                    renderInput={(params) => <TextField {...params} label="Country" />}
                    isOptionEqualToValue={(option, value) => option.code === value.code}
                    fullWidth
                />
                <Autocomplete
                    multiple freeSolo size="small" options={[]}
                    value={currentTemplate?.keywords || []}
                    onChange={(_e, newValue) => setCurrentTemplate(prev => prev ? { ...prev, keywords: newValue as string[] } : null)}
                    renderTags={(value, getTagProps) => value.map((option, index) => <Chip size="small" label={option} {...getTagProps({ index })} key={index} />)}
                    renderInput={(params) => <TextField {...params} label="Keywords" placeholder="Type and press Enter" />}
                />
                <FormControl fullWidth>
                    <InputLabel>Access Level</InputLabel>
                    <Select size="small" label="Access Level" value={currentTemplate?.accessLevel || ''}
                        onChange={e => setCurrentTemplate(prev => prev ? { ...prev, accessLevel: e.target.value } : null)}>
                        <MenuItem value="public">Public</MenuItem>
                        <MenuItem value="private">Private</MenuItem>
                    </Select>
                </FormControl>
            </Box>

            {/* ── HEADER SECTION ── */}
            <WaBizSection title="Header" number={1} optional>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: config.header.type !== 'none' ? 2 : 0 }}>
                    {HEADER_TYPES.map(ht => (
                        <Button key={ht.value} size="small" variant={config.header.type === ht.value ? 'contained' : 'outlined'}
                            startIcon={ht.icon}
                            onClick={() => {
                                updateConfig({ header: { type: ht.value, text: ht.value === 'text' ? (config.header.text || '') : undefined } });
                                if (ht.value === 'none' || ht.value === 'text') {
                                    setHeaderMediaFile(null);
                                    if (headerMediaPreview) { URL.revokeObjectURL(headerMediaPreview); setHeaderMediaPreview(''); }
                                    setCurrentTemplate(prev => prev ? { ...prev, mediaAttachments: [] } : null);
                                }
                            }}
                            sx={{
                                borderColor: config.header.type === ht.value ? WA_BIZ_GREEN : 'divider',
                                backgroundColor: config.header.type === ht.value ? WA_BIZ_GREEN : 'transparent',
                                color: config.header.type === ht.value ? '#fff' : 'text.primary',
                                '&:hover': { backgroundColor: config.header.type === ht.value ? WA_BIZ_GREEN_DARK : 'action.hover' },
                                textTransform: 'none', minWidth: 80,
                            }}
                        >
                            {ht.label}
                        </Button>
                    ))}
                </Box>
                {config.header.type === 'text' && (
                    <TextField size="small" label="Header Text" fullWidth
                        value={config.header.text || ''}
                        onChange={e => updateConfig({ header: { type: 'text', text: e.target.value } })}
                        helperText={`${(config.header.text || '').length}/60 characters. Use {{1}} for variables.`}
                        inputProps={{ maxLength: 60 }}
                    />
                )}
                {(config.header.type === 'image' || config.header.type === 'video' || config.header.type === 'document') && (
                    <Box>
                        <input type="file" accept={getAcceptForHeader()} onChange={handleHeaderMediaChange}
                            style={{ display: 'none' }} id="wa-biz-header-media-upload" />
                        <label htmlFor="wa-biz-header-media-upload">
                            <Button variant="outlined" component="span" startIcon={<AttachFile />}
                                sx={{ borderColor: WA_BIZ_GREEN, color: WA_BIZ_GREEN, '&:hover': { borderColor: WA_BIZ_GREEN_DARK, backgroundColor: 'rgba(37, 211, 102, 0.08)' } }}>
                                Upload {config.header.type === 'image' ? 'Image (JPEG/PNG)' : config.header.type === 'video' ? 'Video (MP4)' : 'Document (PDF)'}
                            </Button>
                        </label>
                        {headerMediaPreview && config.header.type === 'image' && (
                            <Box sx={{ mt: 1, position: 'relative', display: 'inline-block' }}>
                                <img src={headerMediaPreview} alt="Header" style={{ maxHeight: 120, borderRadius: 8, border: '1px solid #ddd' }} />
                                <IconButton size="small" onClick={() => { setHeaderMediaFile(null); URL.revokeObjectURL(headerMediaPreview); setHeaderMediaPreview(''); setCurrentTemplate(prev => prev ? { ...prev, mediaAttachments: [] } : null); }}
                                    sx={{ position: 'absolute', top: -8, right: -8, backgroundColor: 'error.main', color: '#fff', '&:hover': { backgroundColor: 'error.dark' }, width: 24, height: 24 }}>
                                    <Delete sx={{ fontSize: 14 }} />
                                </IconButton>
                            </Box>
                        )}
                        {headerMediaFile && config.header.type !== 'image' && (
                            <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 1, p: 1, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                                {config.header.type === 'video' ? <Videocam sx={{ color: WA_BIZ_GREEN }} /> : <InsertDriveFile sx={{ color: WA_BIZ_GREEN }} />}
                                <Typography variant="body2" sx={{ flex: 1 }}>{headerMediaFile.name}</Typography>
                                <IconButton size="small" onClick={() => { setHeaderMediaFile(null); if (headerMediaPreview) { URL.revokeObjectURL(headerMediaPreview); setHeaderMediaPreview(''); } setCurrentTemplate(prev => prev ? { ...prev, mediaAttachments: [] } : null); }} sx={{ color: 'error.main' }}>
                                    <Delete fontSize="small" />
                                </IconButton>
                            </Box>
                        )}
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                            {config.header.type === 'image' ? 'Recommended: 800x418 pixels. Max 5MB.' : config.header.type === 'video' ? 'Max 16MB. MP4 format only.' : 'Max 100MB. PDF format only.'}
                        </Typography>
                    </Box>
                )}
            </WaBizSection>

            {/* ── BODY SECTION ── */}
            <WaBizSection title="Body" number={2}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                    <Typography variant="caption" color="text.secondary">
                        {bodyValue.length}/1024 characters
                        {bodyValue.length > 1024 && <span style={{ color: '#d32f2f', fontWeight: 700 }}> — Exceeds limit!</span>}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                        <Button size="small" variant="outlined" onClick={insertVariable}
                            sx={{ textTransform: 'none', fontSize: 12, borderColor: WA_BIZ_GREEN, color: WA_BIZ_GREEN, '&:hover': { borderColor: WA_BIZ_GREEN_DARK, backgroundColor: 'rgba(37,211,102,0.08)' } }}>
                            + Variable
                        </Button>
                        <Button size="small" variant={showPreview ? "contained" : "outlined"}
                            startIcon={showPreview ? <Edit /> : <Visibility />}
                            onClick={() => setShowPreview(!showPreview)}
                            sx={{
                                textTransform: 'none', fontSize: 12,
                                backgroundColor: showPreview ? WA_BIZ_GREEN : 'transparent',
                                color: showPreview ? '#fff' : WA_BIZ_GREEN,
                                borderColor: WA_BIZ_GREEN,
                                '&:hover': { backgroundColor: showPreview ? WA_BIZ_GREEN_DARK : 'rgba(37,211,102,0.08)' }
                            }}
                        >
                            {showPreview ? 'Edit' : 'Preview'}
                        </Button>
                    </Box>
                </Box>
                {showPreview ? (
                    <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, overflow: 'hidden' }}>
                        {/* Preview: Header */}
                        {config.header.type !== 'none' && (
                            <Box sx={{ p: 1.5, borderBottom: '1px solid', borderColor: 'divider', backgroundColor: theme => theme.palette.mode === 'dark' ? 'rgba(37,211,102,0.1)' : 'rgba(37,211,102,0.05)' }}>
                                {config.header.type === 'text' && <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>{config.header.text || 'Header text'}</Typography>}
                                {config.header.type === 'image' && (headerMediaPreview ? <img src={headerMediaPreview} alt="Header" style={{ maxWidth: '100%', maxHeight: 200, borderRadius: 8 }} /> : <Box sx={{ height: 120, display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: 'action.hover', borderRadius: 1 }}><ImageOutlined sx={{ fontSize: 40, color: 'text.secondary' }} /></Box>)}
                                {config.header.type === 'video' && <Box sx={{ height: 120, display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: 'action.hover', borderRadius: 1 }}><Videocam sx={{ fontSize: 40, color: 'text.secondary' }} /></Box>}
                                {config.header.type === 'document' && <Box sx={{ height: 80, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, backgroundColor: 'action.hover', borderRadius: 1 }}><InsertDriveFile sx={{ fontSize: 32, color: 'text.secondary' }} /><Typography color="text.secondary">Document</Typography></Box>}
                            </Box>
                        )}
                        {/* Preview: Body */}
                        <Box sx={{ p: 2 }}>
                            <Box sx={{
                                background: theme => theme.palette.mode === 'dark' ? '#1e3a29' : '#dcf8c6',
                                borderRadius: '0 12px 12px 12px', p: 1.5, maxWidth: '90%',
                                '& strong': { fontWeight: 700 }, '& em': { fontStyle: 'italic' },
                                '& del': { textDecoration: 'line-through' },
                                '& code': { fontFamily: 'monospace', backgroundColor: 'rgba(0,0,0,0.06)', padding: '2px 4px', borderRadius: 3 },
                            }}
                                dangerouslySetInnerHTML={{ __html: formatWhatsAppBiz(bodyValue) || '<span style="color:#999">Body text goes here...</span>' }}
                            />
                        </Box>
                        {/* Preview: Footer */}
                        {config.footer && (
                            <Box sx={{ px: 2, pb: 1 }}>
                                <Typography variant="caption" color="text.secondary">{config.footer}</Typography>
                            </Box>
                        )}
                        {/* Preview: Buttons */}
                        {config.buttons.length > 0 && (
                            <Box sx={{ borderTop: '1px solid', borderColor: 'divider' }}>
                                {config.buttons.map((btn, i) => (
                                    <Box key={i} sx={{ textAlign: 'center', py: 1, borderBottom: i < config.buttons.length - 1 ? '1px solid' : 'none', borderColor: 'divider' }}>
                                        <Typography variant="body2" sx={{ color: '#0088cc', fontWeight: 600, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                                            {btn.type === 'URL' && <OpenInNew sx={{ fontSize: 14 }} />}
                                            {btn.type === 'PHONE_NUMBER' && <Phone sx={{ fontSize: 14 }} />}
                                            {btn.type === 'QUICK_REPLY' && <Reply sx={{ fontSize: 14 }} />}
                                            {btn.text || 'Button text'}
                                        </Typography>
                                    </Box>
                                ))}
                            </Box>
                        )}
                    </Box>
                ) : (
                    <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, overflow: 'hidden' }}>
                        <Box sx={{
                            background: theme => theme.palette.mode === 'dark' ? theme.palette.grey[800] : '#f0f2f5',
                            borderBottom: '1px solid', borderColor: 'divider',
                            padding: '6px 12px', display: 'flex', gap: '2px', alignItems: 'center'
                        }}>
                            <ToggleButtonGroup size="small" sx={{ '& .MuiToggleButton-root': { border: 'none', color: theme => theme.palette.mode === 'dark' ? theme.palette.grey[400] : '#54656f', px: 1 } }}>
                                <ToggleButton value="bold" onClick={() => applyFormatting('bold')} sx={{ '&:hover': { color: WA_BIZ_GREEN } }}><FormatBold fontSize="small" /></ToggleButton>
                                <ToggleButton value="italic" onClick={() => applyFormatting('italic')} sx={{ '&:hover': { color: WA_BIZ_GREEN } }}><FormatItalic fontSize="small" /></ToggleButton>
                                <ToggleButton value="strikethrough" onClick={() => applyFormatting('strikethrough')} sx={{ '&:hover': { color: WA_BIZ_GREEN } }}><FormatStrikethrough fontSize="small" /></ToggleButton>
                                <ToggleButton value="monospace" onClick={() => applyFormatting('monospace')} sx={{ '&:hover': { color: WA_BIZ_GREEN } }}><Code fontSize="small" /></ToggleButton>
                            </ToggleButtonGroup>
                        </Box>
                        <TextField
                            multiline minRows={6} maxRows={12}
                            value={bodyValue}
                            onChange={e => { setBodyValue(e.target.value); setCurrentTemplate(prev => prev ? { ...prev, templateContent: e.target.value } : null); }}
                            onSelect={e => { const t = e.target as HTMLTextAreaElement; setSelection({ start: t.selectionStart, end: t.selectionEnd }); }}
                            inputRef={bodyRef}
                            variant="standard" fullWidth
                            InputProps={{ disableUnderline: true, style: { padding: '12px 16px', fontSize: '14px', lineHeight: '1.5' } }}
                            sx={{ '& .MuiInputBase-root': { minHeight: '150px', color: theme => theme.palette.text.primary, backgroundColor: theme => theme.palette.background.paper }, width: '100%' }}
                            placeholder="Enter the body text of your message. Use {{1}}, {{2}}, etc. for variables."
                            error={bodyValue.length > 1024}
                        />
                    </Box>
                )}
                <Box sx={{
                    mt: 1.5, p: 1.5, borderRadius: 1,
                    backgroundColor: theme => theme.palette.mode === 'dark' ? 'rgba(37,211,102,0.12)' : 'rgba(37,211,102,0.06)',
                    border: '1px solid', borderColor: theme => theme.palette.mode === 'dark' ? 'rgba(37,211,102,0.25)' : 'rgba(37,211,102,0.15)',
                }}>
                    <Typography variant="caption" color="text.secondary" component="div">
                        <strong>Formatting:</strong> *bold* &nbsp; _italic_ &nbsp; ~strikethrough~ &nbsp; {'```monospace```'}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                        <strong>Variables:</strong> Use {'{{1}}'}, {'{{2}}'}, {'{{3}}'} etc. Variables must be sequential starting from 1, with no gaps. Do not place variables at the start/end of sentences without surrounding text, and do not place two variables adjacent to each other.
                    </Typography>
                </Box>
            </WaBizSection>

            {/* ── FOOTER SECTION ── */}
            <WaBizSection title="Footer" number={3} optional>
                <TextField size="small" label="Footer Text" fullWidth
                    value={config.footer}
                    onChange={e => updateConfig({ footer: e.target.value })}
                    helperText={`${(config.footer || '').length}/60 characters`}
                    inputProps={{ maxLength: 60 }}
                    placeholder="e.g. Reply STOP to unsubscribe"
                />
            </WaBizSection>

            {/* ── BUTTONS SECTION ── */}
            <WaBizSection title="Buttons" number={4} optional>
                {config.buttons.map((btn, idx) => (
                    <Box key={idx} sx={{ mb: 2, p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 1.5, backgroundColor: theme => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.01)' }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                            <Typography variant="caption" sx={{ fontWeight: 700 }}>Button {idx + 1}</Typography>
                            <IconButton size="small" onClick={() => removeButton(idx)} sx={{ color: 'error.main' }}>
                                <Remove fontSize="small" />
                            </IconButton>
                        </Box>
                        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 2fr' }, gap: 1.5 }}>
                            <TextField select size="small" label="Type" fullWidth value={btn.type}
                                onChange={e => updateButton(idx, { type: e.target.value as WhatsAppBusinessButton['type'], url: undefined, phoneNumber: undefined })}>
                                {BUTTON_TYPES.map(bt => <MenuItem key={bt.value} value={bt.value}><Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>{bt.icon}{bt.label}</Box></MenuItem>)}
                            </TextField>
                            <TextField size="small" label="Button Text" fullWidth value={btn.text}
                                onChange={e => updateButton(idx, { text: e.target.value })}
                                inputProps={{ maxLength: 25 }}
                                helperText={`${(btn.text || '').length}/25`}
                            />
                        </Box>
                        {btn.type === 'URL' && (
                            <TextField size="small" label="URL" fullWidth value={btn.url || ''} onChange={e => updateButton(idx, { url: e.target.value })}
                                placeholder="https://www.example.com/{{1}}" sx={{ mt: 1.5 }}
                                helperText="Use {{1}} for a dynamic URL suffix." />
                        )}
                        {btn.type === 'PHONE_NUMBER' && (
                            <TextField size="small" label="Phone Number" fullWidth value={btn.phoneNumber || ''} onChange={e => updateButton(idx, { phoneNumber: e.target.value })}
                                placeholder="+1234567890" sx={{ mt: 1.5 }}
                                helperText="Include country code." />
                        )}
                    </Box>
                ))}
                {config.buttons.length < 3 && (
                    <Button variant="outlined" startIcon={<Add />} onClick={addButton} fullWidth
                        sx={{ borderStyle: 'dashed', textTransform: 'none', borderColor: WA_BIZ_GREEN, color: WA_BIZ_GREEN, '&:hover': { borderColor: WA_BIZ_GREEN_DARK, backgroundColor: 'rgba(37,211,102,0.06)' } }}>
                        Add Button ({config.buttons.length}/3)
                    </Button>
                )}
            </WaBizSection>

            {/* Meta status display */}
            {currentTemplate?.metaStatus && (
                <Paper variant="outlined" sx={{ p: 1.5, mt: 2, borderRadius: 2, display: 'flex', alignItems: 'center', gap: 1,
                    borderColor: currentTemplate.metaStatus === 'APPROVED' ? 'success.main' : currentTemplate.metaStatus === 'REJECTED' ? 'error.main' : 'warning.main',
                    backgroundColor: theme => currentTemplate.metaStatus === 'APPROVED' ? (theme.palette.mode === 'dark' ? 'rgba(46,125,50,0.1)' : 'rgba(46,125,50,0.05)') : currentTemplate.metaStatus === 'REJECTED' ? (theme.palette.mode === 'dark' ? 'rgba(211,47,47,0.1)' : 'rgba(211,47,47,0.05)') : (theme.palette.mode === 'dark' ? 'rgba(237,108,2,0.1)' : 'rgba(237,108,2,0.05)')
                }}>
                    <Chip size="small" label={currentTemplate.metaStatus}
                        color={currentTemplate.metaStatus === 'APPROVED' ? 'success' : currentTemplate.metaStatus === 'REJECTED' ? 'error' : 'warning'}
                        sx={{ fontWeight: 700 }} />
                    {currentTemplate.metaStatusMessage && <Typography variant="caption" color="text.secondary">{currentTemplate.metaStatusMessage}</Typography>}
                </Paper>
            )}
        </Box>
    );
};
